import type { AxiosInstance } from 'axios';
import type {
  ChallengeAdminTeam, ChallengeBoard, ChallengeBoardKind, ChallengeFlag, ChallengeMe, ChallengeOrganizerHit, ChallengeRide, ChallengeSummary,
  ChallengeTeam, OrganizerChallenge,
} from './types';
import { ChallengeJoinDraft, ChallengeRideDraft, parseKm } from './challenge';

/** Veranstalter, den ein Team beim Gründen anlegt: dieselben Felder wie „Veranstalter eintragen“. */
export interface ChallengeNewOrganizerDraft { name: string; website: string; email: string; postalCode: string; city: string }

/**
 * Abrufe der Winterchallenge (`/api/v1/challenge/current/**`), für Web und App. Formular → Request
 * steht hier, damit beide dasselbe schicken.
 */
export function rideRequestOf(draft: ChallengeRideDraft) {
  return {
    rideDate: draft.rideDate,
    km: parseKm(draft.km).km ?? 0,
    bikeType: draft.bikeType,
    proofUrl: draft.proofUrl.trim() || null,
  };
}

export function createChallengeService(api: AxiosInstance) {
  const base = '/challenge/current';
  return {
    teamFriend: async (slug: string, participantId: number, action: 'request' | 'accept' | 'remove'): Promise<ChallengeTeam> =>
      (await api.post(`${base}/teams/${encodeURIComponent(slug)}/friends/${participantId}/${action}`)).data,
    summary: async (): Promise<ChallengeSummary> => (await api.get(base)).data,

    /** `null`, wenn das Konto (noch) nicht mitmacht. */
    me: async (): Promise<ChallengeMe | null> => {
      const r = await api.get(`${base}/me`);
      return r.status === 204 ? null : r.data;
    },

    join: async (draft: ChallengeJoinDraft): Promise<ChallengeMe> =>
      (await api.post(`${base}/join`, { displayName: draft.displayName, bundesland: draft.bundesland, consent: draft.consent })).data,

    rename: async (displayName: string): Promise<ChallengeMe> =>
      (await api.put(`${base}/me/name`, { displayName })).data,

    addRide: async (draft: ChallengeRideDraft): Promise<ChallengeMe> => (await api.post(`${base}/rides`, rideRequestOf(draft))).data,
    updateRide: async (id: number, draft: ChallengeRideDraft): Promise<ChallengeMe> =>
      (await api.put(`${base}/rides/${id}`, rideRequestOf(draft))).data,
    deleteRide: async (id: number): Promise<ChallengeMe> => (await api.delete(`${base}/rides/${id}`)).data,

    board: async (kind: ChallengeBoardKind, offset = 0, limit = 10): Promise<ChallengeBoard> =>
      (await api.get(`${base}/boards/${kind}`, { params: { offset, limit } })).data,

    report: async (participantId: number): Promise<void> => { await api.post(`${base}/participants/${participantId}/report`); },

    team: async (slug: string): Promise<ChallengeTeam> => (await api.get(`${base}/teams/${encodeURIComponent(slug)}`)).data,
    /** Mit Veranstalter (vorhanden oder neu) heißt das Team wie er; `name` gilt nur ohne. */
    createTeam: async (req: { name: string; abbreviation: string; description: string; organizerId: number | null; newOrganizer?: ChallengeNewOrganizerDraft | null }): Promise<ChallengeTeam> => {
      const opt = (v: string) => v.trim() || null;
      const o = req.newOrganizer;
      return (await api.post(`${base}/teams`, {
        name: req.name, abbreviation: req.abbreviation, description: req.description.trim() || null, organizerId: req.organizerId,
        newOrganizer: o ? { name: o.name.trim(), website: opt(o.website), email: opt(o.email), postalCode: opt(o.postalCode), city: opt(o.city) } : null,
      })).data;
    },
    joinTeam: async (slug: string, code: string): Promise<ChallengeTeam> =>
      (await api.post(`${base}/teams/${encodeURIComponent(slug)}/join`, { code })).data,

    /** Beitritt ohne Einladungslink anfragen; der Gründer nimmt an oder lehnt ab. */
    requestTeam: async (slug: string): Promise<ChallengeTeam> =>
      (await api.post(`${base}/teams/${encodeURIComponent(slug)}/requests`)).data,
    withdrawTeamRequest: async (slug: string): Promise<ChallengeTeam> =>
      (await api.delete(`${base}/teams/${encodeURIComponent(slug)}/requests/mine`)).data,
    /** Nur der Gründer. Antwort: die Teamseite mit den übrigen Anfragen. */
    decideTeamRequest: async (slug: string, requestId: number, accept: boolean): Promise<ChallengeTeam> =>
      (await api.post(`${base}/teams/${encodeURIComponent(slug)}/requests/${requestId}/decision`, { accept })).data,

    searchOrganizers: async (q: string): Promise<ChallengeOrganizerHit[]> =>
      (await api.get(`${base}/organizer-search`, { params: { q } })).data,

    /** `null`, wenn die Veranstalterseite nichts zeigen soll. */
    forOrganizer: async (organizerId: number): Promise<OrganizerChallenge | null> => {
      const r = await api.get(`${base}/organizers/${organizerId}`);
      return r.status === 204 ? null : r.data;
    },

    // ---- Redaktion ----
    flags: async (): Promise<ChallengeFlag[]> => (await api.get('/admin/challenge/flags')).data,
    /** Alle Teilnehmer der laufenden Saison mit Kennzahlen. */
    participants: async (): Promise<ChallengeFlag[]> => (await api.get('/admin/challenge/participants')).data,
    setHidden: async (participantId: number, hidden: boolean): Promise<void> => {
      await api.put(`/admin/challenge/participants/${participantId}/hidden`, { hidden });
    },
    /** Alle Fahrten eines Teilnehmers; Bearbeiten und Löschen liefern die neue Liste. */
    participantRides: async (participantId: number): Promise<ChallengeRide[]> =>
      (await api.get(`/admin/challenge/participants/${participantId}/rides`)).data,
    updateRideAsEditor: async (rideId: number, draft: ChallengeRideDraft): Promise<ChallengeRide[]> =>
      (await api.put(`/admin/challenge/rides/${rideId}`, rideRequestOf(draft))).data,
    deleteRideAsEditor: async (rideId: number): Promise<ChallengeRide[]> =>
      (await api.delete(`/admin/challenge/rides/${rideId}`)).data,
    /** Alle Teams der Saison, auch ausgeblendete. */
    adminTeams: async (): Promise<ChallengeAdminTeam[]> => (await api.get('/admin/challenge/teams')).data,
    /** Umbenennen und ausblenden; `null`/`undefined` heißt „unverändert“. Liefert das geänderte Team. */
    updateTeamAsEditor: async (teamId: number, patch: { name?: string | null; hidden?: boolean | null }): Promise<ChallengeAdminTeam> =>
      (await api.put(`/admin/challenge/teams/${teamId}`, patch)).data,
  };
}

export type ChallengeService = ReturnType<typeof createChallengeService>;
